import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlarmClock, CalendarX2, Play } from 'lucide-react';
import type { GoalConfigSettings } from '@pms/shared';
import { goalsService } from '@/services/goals.service';
import { getErrorMessage } from '@/lib/errors';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { useGoalConfig } from './useGoalConfig';

const OFFSET_PRESETS = [1, 3, 7, 14, 30];

function formatIst(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' });
}

function Form({ initial }: { initial: GoalConfigSettings }) {
  const queryClient = useQueryClient();
  const { save, saving } = useGoalConfig();

  const [sweepEnabled, setSweepEnabled] = useState(initial.sweep_enabled);
  const [runTime, setRunTime] = useState(initial.sweep_run_time);
  const [autoMiss, setAutoMiss] = useState(initial.auto_miss_enabled);
  const [reminders, setReminders] = useState(initial.reminder_enabled);
  const [offsets, setOffsets] = useState<number[]>(initial.reminder_offset_days);

  const sameOffsets = offsets.length === initial.reminder_offset_days.length
    && offsets.every((d, i) => d === initial.reminder_offset_days[i]);
  const dirty = sweepEnabled !== initial.sweep_enabled
    || runTime !== initial.sweep_run_time
    || autoMiss !== initial.auto_miss_enabled
    || reminders !== initial.reminder_enabled
    || !sameOffsets;

  const validTime = /^([01]\d|2[0-3]):[0-5]\d$/.test(runTime);
  const canSave = dirty && validTime && offsets.length > 0 && !saving;

  const runNow = useMutation({
    mutationFn: () => goalsService.runSweep(),
    onSuccess: (r) => {
      if (r.skipped === 'globally_disabled') toast.info('Automation is switched off — nothing ran');
      else toast.success(`Marked ${r.missed} missed · sent ${r.reminders} reminder(s)`);
      void queryClient.invalidateQueries({ queryKey: ['goals'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  function toggleOffset(day: number) {
    setOffsets((prev) => (prev.includes(day)
      ? prev.filter((d) => d !== day)
      : [...prev, day].sort((a, b) => b - a)));
  }

  return (
    <div className="max-w-2xl space-y-4">
      {/* What this automation actually does — it is otherwise invisible to admins. */}
      <Card variant="flat">
        <CardHeader><CardTitle>What runs automatically</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-3">
            <CalendarX2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Auto-miss overdue goals</span>
                <Badge variant={autoMiss && sweepEnabled ? 'default' : 'secondary'}>
                  {autoMiss && sweepEnabled ? 'On' : 'Off'}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Active goals past their due date are marked missed, an audit entry is recorded
                against the system, and the owner is notified. Reopening a goal will not re-miss
                it — move the due date to put it back under automation.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <AlarmClock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Deadline reminders</span>
                <Badge variant={reminders && sweepEnabled ? 'default' : 'secondary'}>
                  {reminders && sweepEnabled ? 'On' : 'Off'}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Owners of active goals are notified the configured number of days before the due
                date — at most once per goal per day.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card variant="flat">
        <CardHeader><CardTitle>Schedule</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <Label>Enable automation</Label>
              <p className="text-xs text-muted-foreground">Master switch for everything on this tab.</p>
            </div>
            <Switch checked={sweepEnabled} onChange={setSweepEnabled} aria-label="Enable automation" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="goal-sweep-time">Run time</Label>
            <Input
              id="goal-sweep-time"
              type="time"
              value={runTime}
              disabled={!sweepEnabled}
              onChange={(e) => setRunTime(e.target.value)}
              className={cn('sm:w-40', !validTime && 'border-destructive')}
            />
            {!validTime && <p className="text-xs text-destructive">Use HH:MM (00:00–23:59)</p>}
            <p className="text-[11px] text-muted-foreground">
              All times are IST (Asia/Kolkata). The engine checks every 5 minutes and runs once
              this time has passed, so a run missed during a restart still happens.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card variant="flat">
        <CardHeader><CardTitle>Rules</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <Label>Mark overdue goals as missed</Label>
            <Switch checked={autoMiss} onChange={setAutoMiss} aria-label="Mark overdue goals as missed" />
          </div>
          <div className="flex items-center justify-between gap-3">
            <Label>Send deadline reminders</Label>
            <Switch checked={reminders} onChange={setReminders} aria-label="Send deadline reminders" />
          </div>

          <div className="space-y-1.5">
            <Label>Remind this many days before the due date</Label>
            <div className="flex flex-wrap gap-2">
              {OFFSET_PRESETS.map((day) => (
                <button
                  key={day}
                  type="button"
                  disabled={!reminders}
                  onClick={() => toggleOffset(day)}
                  className={cn(
                    'min-h-[34px] rounded-full border px-3 text-sm transition-colors disabled:opacity-50',
                    offsets.includes(day)
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:bg-muted/40',
                  )}
                >
                  {day === 1 ? '1 day' : `${day} days`}
                </button>
              ))}
            </div>
            {offsets.length === 0
              ? <p className="text-xs text-destructive">Pick at least one reminder offset.</p>
              : <p className="text-[11px] text-muted-foreground">Currently: {offsets.join(', ')} day(s) before.</p>}
          </div>
        </CardContent>
      </Card>

      <Card variant="flat">
        <CardHeader><CardTitle>Last run</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {initial.last_run_at ? (
            <div className="text-sm">
              <p className="font-medium">{formatIst(initial.last_run_at)} IST</p>
              <p className="text-xs text-muted-foreground">{initial.last_run_summary}</p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              This sweep hasn’t done any work yet. Quiet runs aren’t recorded.
            </p>
          )}
          <Button variant="outline" disabled={runNow.isPending} onClick={() => runNow.mutate()}>
            <Play className="h-4 w-4" /> {runNow.isPending ? 'Running…' : 'Run now'}
          </Button>
          <p className="text-[11px] text-muted-foreground">
            Running now ignores the scheduled time but still respects the switches above.
          </p>
        </CardContent>
      </Card>

      <Button
        disabled={!canSave}
        onClick={() => save({
          sweep_enabled: sweepEnabled,
          sweep_run_time: runTime,
          auto_miss_enabled: autoMiss,
          reminder_enabled: reminders,
          reminder_offset_days: offsets,
        })}
      >
        {saving ? 'Saving…' : 'Save changes'}
      </Button>
    </div>
  );
}

/**
 * The goal automation ("time section") settings. Every value here is read fresh by the
 * scheduler on its next tick, so a change applies without restarting the server.
 */
export function GoalAutomationTab() {
  const { data, isLoading, isError, refetch } = useGoalConfig();

  if (isLoading) {
    return (
      <div className="max-w-2xl space-y-3">
        {[...Array(4)].map((_, i) => <div key={i} className="h-28 animate-pulse rounded-xl bg-muted/60" />)}
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="rounded-xl border border-border/60 p-8 text-center text-sm text-muted-foreground">
        Couldn’t load automation settings.{' '}
        <button type="button" className="text-primary underline" onClick={refetch}>Retry</button>
      </div>
    );
  }
  // Remount on saved values so the local form state re-seeds from the server copy.
  return <Form key={JSON.stringify(data)} initial={data} />;
}
